// covered-by: qa/rickrubin/tests/api/ask-puntt-projects.spec.ts
// covered-by: packages/schemas/src/christo/__tests__/askPunttProject.test.ts
import { z } from "zod";

import {
  askPunttChatSchema,
  askPunttChatUserSummarySchema,
  getAskPunttChatHistoryResponseSchema,
} from "./askPuntt";
import { askPunttAreaSchema } from "./askPunttArea";
import {
  askPunttListSortBySchema,
  askPunttOrgRegionsQuerySchema,
  askPunttOrgRegionsSchema,
  askPunttOrgTagsQuerySchema,
  askPunttOrgTagsSchema,
} from "./askPunttOrgLabels";

// A single stored version of a project file.
export const askPunttProjectFileVersionSchema = z.object({
  versionId: z.string(),
  versionNumber: z.number(),
  key: z.string(),
  source: z.string().url(),
  name: z.string().optional(),
  uploadedBy: z.string(),
  uploadedAt: z.union([z.string(), z.date()]),
  label: z.string().optional(),
  // Agent-written summary of this specific version.
  description: z.string().optional(),
  // Auto-review linkage (P1/P4). Null until a review is enqueued for the version.
  reviewStatus: z.string().nullable().optional(),
  reviewTicketId: z.string().nullable().optional(),
  reviewWorkflowId: z.string().nullable().optional(),
  lockedAt: z.union([z.string(), z.date()]).nullable().optional(),
  // Derived cache from approvalEvents — never authoritative.
  participationSnapshot: z
    .record(z.string(), z.unknown())
    .nullable()
    .optional(),
});
export type AskPunttProjectFileVersion = z.infer<
  typeof askPunttProjectFileVersionSchema
>;

// A project file with its full version history.
export const askPunttProjectFileSchema = z.object({
  fileId: z.string(),
  name: z.string(),
  // Overall description of the document (combined across versions, recent
  // versions weighted more). Shown in the campaign docs panel.
  description: z.string().optional(),
  type: z.enum(["image", "video", "file"]),
  currentVersion: z.number(),
  versions: z.array(askPunttProjectFileVersionSchema),
  /** Open org labels on the document (not per-version). */
  regions: z.array(z.string()).optional().default([]),
  tags: z.array(z.string()).optional().default([]),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type AskPunttProjectFile = z.infer<typeof askPunttProjectFileSchema>;

// A committed project instruction ("memory").
export const askPunttProjectInstructionSchema = z.object({
  instructionId: z.string(),
  text: z.string(),
  authorId: z.string(),
  authorKind: z.enum(["user", "puntt"]),
  sourceChatId: z.string().nullable().optional(),
  sourceMessageId: z.string().nullable().optional(),
  scope: z.enum(["project", "enterprise"]),
  createdAt: z.union([z.string(), z.date()]),
  revokedAt: z.union([z.string(), z.date()]).nullable().optional(),
});
export type AskPunttProjectInstruction = z.infer<
  typeof askPunttProjectInstructionSchema
>;

// A surfaced agent scratch-pad entry as returned by the API. The agent keeps an
// arbitrary, persistent scratch pad on the project (its own working memory);
// only entries it flagged `display: true` are serialized here — agent-private
// notes are never sent to the client. Read-only: users cannot create or edit
// these (the agent maintains them via the `update_project_scratchpad` tool).
export const askPunttProjectScratchpadEntrySchema = z.object({
  key: z.string(),
  label: z.string().nullable().optional(),
  value: z.string(),
  updatedAt: z.union([z.string(), z.date()]),
});
export type AskPunttProjectScratchpadEntry = z.infer<
  typeof askPunttProjectScratchpadEntrySchema
>;

// A private chat shared INTO the project (provenance for the ingestion that
// merges the private conversation into the shared chat as a new message).
export const askPunttProjectSharedSourceChatSchema = z.object({
  chatId: z.string(),
  sharedBy: z.string(),
  sharedAt: z.union([z.string(), z.date()]),
  ingestStatus: z.enum(["pending", "ingested", "failed"]).optional(),
});
export type AskPunttProjectSharedSourceChat = z.infer<
  typeof askPunttProjectSharedSourceChatSchema
>;

/**
 * The areas one project member owns.
 *
 * why a separate list and not a field on `members`: the API's `members` array
 * is a hydrated, display-only summary built by the handler, and it is shared
 * with chats (`askPunttChatUserSummarySchema`). Ownership is stored state that
 * an owner writes and the router reads, so it keeps its own shape and its own
 * list — the same way `allowList` does. `allowList` stays the access list.
 */
export const askPunttProjectMemberAreaSchema = z.object({
  userId: z.string(),
  /** Empty means the member owns nothing named; routing falls back to owner. */
  owns: z.array(askPunttAreaSchema).default([]),
});
export type AskPunttProjectMemberArea = z.infer<
  typeof askPunttProjectMemberAreaSchema
>;

// Full project document as returned by the API.
export const askPunttProjectSchema = z.object({
  _id: z.string(),
  enterpriseId: z.string(),
  name: z.string(),
  description: z.string(),
  context: z.string(),
  upcomingWork: z.string(),
  ownerId: z.string(),
  sharedChatId: z.string().nullable(),
  // Private chats shared into the project (provenance + ingest status).
  sharedSourceChats: z.array(askPunttProjectSharedSourceChatSchema).optional(),
  // Hydrated member summaries (handlers turn allowList ids into these).
  members: z.array(askPunttChatUserSummarySchema).optional(),
  // Which areas each member owns. Absent on projects written before this
  // field existed, and on rows where nothing has been set.
  memberAreas: z.array(askPunttProjectMemberAreaSchema).optional(),
  // Files omitted from list responses (heavy); present on getProject.
  files: z.array(askPunttProjectFileSchema).optional(),
  // Active committed instructions (present on getProject; list rows omit).
  instructions: z.array(askPunttProjectInstructionSchema).optional(),
  // Surfaced agent scratch-pad entries (display:true only; agent-private notes
  // are never sent to the client). Present on getProject; list rows omit.
  agentScratchpad: z.array(askPunttProjectScratchpadEntrySchema).optional(),
  // Project-wide auto-review pause flag (§12).
  autoReviewPaused: z.boolean().optional().default(false),
  // Linked PM-tool task for comment sync (§13).
  linkedPmTask: z
    .object({
      provider: z.enum(["wrike", "asana"]),
      taskId: z.string(),
      taskUrl: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  // Advisory participation rollup across assets (derived; never a gate).
  approvalRollup: z.record(z.string(), z.unknown()).nullable().optional(),
  /** Multi-value geography labels for org filters (e.g. NA, EU). */
  regions: z.array(z.string()).optional().default([]),
  /** Open-vocabulary org tags for search/filter. */
  tags: z.array(z.string()).optional().default([]),
  /**
   * Distinct tags from nested files, rolled up for list/card display.
   * Not persisted; never copied onto `tags`.
   */
  fileTags: z.array(z.string()).optional().default([]),
  // "owner" when the requester owns the project, "member" via allowList.
  role: z.enum(["owner", "member"]).optional(),
  // Coarse, list-friendly review status for the project, derived from its
  // file-version review status + hydrated review tickets. Only populated when
  // the caller opts in (`includeReviewState`) — heavy ticket hydration is
  // skipped otherwise. "none" means the project has no reviews yet.
  //   in_review          — at least one review is still running
  //   changes_requested  — a completed review surfaced findings/comments
  //   approved           — every review completed clean (no findings)
  //   none               — no reviews
  reviewState: z
    .enum(["in_review", "changes_requested", "approved", "none"])
    .optional(),
  /** Shared-chat message unread for the requester (list endpoints only). */
  hasUnread: z.boolean().optional(),
  /** Set when the whole project is marked done. Null while it is still open. */
  doneAt: z.union([z.string(), z.date()]).nullable().optional(),
  doneByUserId: z.string().nullable().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type AskPunttProject = z.infer<typeof askPunttProjectSchema>;
export type AskPunttProjectReviewState = NonNullable<
  AskPunttProject["reviewState"]
>;

// Light list row (no files / instructions / scratch-pad arrays).
export const askPunttProjectListItemSchema = askPunttProjectSchema.omit({
  files: true,
  instructions: true,
  agentScratchpad: true,
});
export type AskPunttProjectListItem = z.infer<
  typeof askPunttProjectListItemSchema
>;

// --- Request/Response Schemas ---

export const askPunttProjectScopeSchema = z.enum(["mine", "shared", "all"]);
export type AskPunttProjectScope = z.infer<typeof askPunttProjectScopeSchema>;

// List projects
export const listAskPunttProjectsRequestSchema = z.object({
  query: z.object({
    limit: z.coerce.number().optional().default(20),
    offset: z.coerce.number().optional().default(0),
    scope: askPunttProjectScopeSchema.optional().default("mine"),
    /** Case-insensitive substring match on project name and description. */
    search: z.string().trim().min(1).max(200).optional(),
    /**
     * Filter to projects that include every listed user as owner or on
     * allowList (AND). Repeat the query param or pass a single id.
     */
    participantIds: z
      .union([z.string(), z.array(z.string()).max(50)])
      .optional()
      .transform((value) => {
        if (value == null) return undefined;
        const raw = Array.isArray(value) ? value : [value];
        const ids = Array.from(
          new Set(raw.map((id) => id.trim()).filter((id) => id.length > 0)),
        );
        return ids.length > 0 ? ids : undefined;
      }),
    /**
     * Opt-in to compute each project's coarse `reviewState`. Off by default
     * because it hydrates review tickets; the Ask Puntt sidebar opts in.
     * NOTE: do NOT use z.coerce.boolean() — it coerces the string "false" to
     * true. Match the literal "true" instead.
     */
    includeReviewState: z
      .union([z.boolean(), z.string()])
      .optional()
      .transform((value) => value === true || value === "true"),
    /** Match projects that include any of these regions (OR). */
    regions: askPunttOrgRegionsQuerySchema,
    /** Match projects that include any of these tags (OR). */
    tags: askPunttOrgTagsQuerySchema,
    sortBy: askPunttListSortBySchema.optional().default("updatedAt"),
  }),
});
export const listAskPunttProjectsResponseSchema = z.object({
  projects: z.array(askPunttProjectListItemSchema),
  total: z.number().default(0),
});
export type ListAskPunttProjectsResponse = z.infer<
  typeof listAskPunttProjectsResponseSchema
>;

// A single chat row as surfaced in a project (the shared chat + the
// requester's own personal chats). Reused by getProject, list, and share.
export const askPunttProjectChatSummarySchema = askPunttChatSchema
  .pick({
    _id: true,
    title: true,
    workflowType: true,
    userId: true,
    updatedAt: true,
  })
  .extend({
    projectChatKind: z.enum(["shared", "personal"]).nullable().optional(),
    createdAt: z.string().optional(),
    hasUnread: z.boolean().optional(),
  });
export type AskPunttProjectChatSummary = z.infer<
  typeof askPunttProjectChatSummarySchema
>;

// Get one project (with its chats)
export const getAskPunttProjectRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
});
export const getAskPunttProjectResponseSchema = z.object({
  project: askPunttProjectSchema,
  // The shared chat + the requester's own personal chats in this project.
  chats: z.array(askPunttProjectChatSummarySchema),
});
export type GetAskPunttProjectResponse = z.infer<
  typeof getAskPunttProjectResponseSchema
>;

// Create project (auto-creates the shared chat)
export const createAskPunttProjectRequestSchema = z.object({
  body: z.object({
    name: z.string().min(1),
    description: z.string().optional().default(""),
    context: z.string().optional().default(""),
    upcomingWork: z.string().optional().default(""),
    regions: askPunttOrgRegionsSchema.optional(),
    tags: askPunttOrgTagsSchema.optional(),
    // Routes the shared chat to warhol. Omitted means rembrandt.
    agent: z.enum(["rembrandt", "warhol"]).optional(),
  }),
});
export const createAskPunttProjectResponseSchema = z.object({
  project: askPunttProjectSchema,
  sharedChatId: z.string(),
  /** Serialized shared-chat history so the workspace can render the starter exchange immediately. */
  sharedChatBootstrap: getAskPunttChatHistoryResponseSchema.optional(),
});
export type CreateAskPunttProjectResponse = z.infer<
  typeof createAskPunttProjectResponseSchema
>;

/** Matches chat title cap (`MAX_ASK_PUNTT_CHAT_TITLE_LENGTH`) and FE breadcrumb input. */
export const MAX_ASK_PUNTT_PROJECT_NAME_LENGTH = 200;

// Update project metadata
export const updateAskPunttProjectRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
  body: z
    .object({
      name: z.string().min(1).max(MAX_ASK_PUNTT_PROJECT_NAME_LENGTH).optional(),
      description: z.string().optional(),
      context: z.string().optional(),
      upcomingWork: z.string().optional(),
      autoReviewPaused: z.boolean().optional(),
      /** Full replace; `[]` clears. */
      regions: askPunttOrgRegionsSchema.optional(),
      /** Full replace; `[]` clears. */
      tags: askPunttOrgTagsSchema.optional(),
    })
    .refine((b) => Object.keys(b).length > 0, {
      message: "At least one field must be provided",
    }),
});
export const updateAskPunttProjectResponseSchema = z.object({
  project: askPunttProjectSchema,
});
export type UpdateAskPunttProjectResponse = z.infer<
  typeof updateAskPunttProjectResponseSchema
>;

/** Patch org labels on a single project file (document-level, not version). */
export const updateAskPunttProjectFileRequestSchema = z.object({
  params: z.object({ projectId: z.string(), fileId: z.string() }),
  body: z
    .object({
      regions: askPunttOrgRegionsSchema.optional(),
      tags: askPunttOrgTagsSchema.optional(),
    })
    .refine((b) => Object.keys(b).length > 0, {
      message: "At least one field must be provided",
    }),
});
export const updateAskPunttProjectFileResponseSchema = z.object({
  file: askPunttProjectFileSchema,
});
export type UpdateAskPunttProjectFileResponse = z.infer<
  typeof updateAskPunttProjectFileResponseSchema
>;

/** Flattened file row for the cross-project files launcher. */
export const askPunttFileListItemSchema = z.object({
  fileId: z.string(),
  name: z.string(),
  type: z.enum(["image", "video", "file"]),
  currentVersion: z.number(),
  uploadedBy: z.string(),
  uploadedAt: z.union([z.string(), z.date()]),
  projectId: z.string(),
  projectName: z.string(),
  /** Effective regions (file's own, or inherited from project when empty). */
  regions: z.array(z.string()).default([]),
  /** File's own tags only (never inherited). */
  tags: z.array(z.string()).default([]),
  reviewStatus: z.string().nullable().optional(),
});
export type AskPunttFileListItem = z.infer<typeof askPunttFileListItemSchema>;

export const listAskPunttFilesRequestSchema = z.object({
  query: z.object({
    limit: z.coerce.number().optional().default(20),
    offset: z.coerce.number().optional().default(0),
    scope: askPunttProjectScopeSchema.optional().default("all"),
    search: z.string().trim().min(1).max(200).optional(),
    participantIds: z
      .union([z.string(), z.array(z.string()).max(50)])
      .optional()
      .transform((value) => {
        if (value == null) return undefined;
        const raw = Array.isArray(value) ? value : [value];
        const ids = Array.from(
          new Set(raw.map((id) => id.trim()).filter((id) => id.length > 0)),
        );
        return ids.length > 0 ? ids : undefined;
      }),
    regions: askPunttOrgRegionsQuerySchema,
    tags: askPunttOrgTagsQuerySchema,
    /** When set, only files whose current version was uploaded by this user. */
    uploadedBy: z.string().trim().min(1).optional(),
    sortBy: askPunttListSortBySchema.optional().default("uploadedAt"),
  }),
});
export const listAskPunttFilesResponseSchema = z.object({
  files: z.array(askPunttFileListItemSchema),
  total: z.number().default(0),
});
export type ListAskPunttFilesResponse = z.infer<
  typeof listAskPunttFilesResponseSchema
>;

// Delete project (soft delete)
export const deleteAskPunttProjectRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
});
export const deleteAskPunttProjectResponseSchema = z.object({
  success: z.boolean(),
});
export type DeleteAskPunttProjectResponse = z.infer<
  typeof deleteAskPunttProjectResponseSchema
>;

const askPunttInviteSummarySchema = z.object({
  recipientUserIds: z.array(z.string()),
  recipientEmails: z.array(z.string()),
  recipientTiers: z.array(z.enum(["project", "enterprise", "new_email"])),
});

// Add member(s) (owner, or an enterprise coordinator who can reach the project) — see services/christo/app/handlers/catalyst/askPunttProjectAuthority.ts
export const addAskPunttProjectMemberRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
  body: z
    .object({
      userIds: z.array(z.string()).max(50).optional(),
      emails: z.array(z.string().email()).max(50).optional(),
    })
    .refine((b) => (b.userIds?.length ?? 0) + (b.emails?.length ?? 0) >= 1, {
      message: "At least one userId or email must be provided",
    }),
});
export const addAskPunttProjectMemberResponseSchema = z.object({
  members: z.array(askPunttChatUserSummarySchema),
  inviteSummary: askPunttInviteSummarySchema.optional(),
});
export type AddAskPunttProjectMemberResponse = z.infer<
  typeof addAskPunttProjectMemberResponseSchema
>;

// Remove a member (owner, or an enterprise coordinator who can reach the project)
export const removeAskPunttProjectMemberRequestSchema = z.object({
  params: z.object({ projectId: z.string(), userId: z.string() }),
});
export const removeAskPunttProjectMemberResponseSchema = z.object({
  members: z.array(askPunttChatUserSummarySchema),
});
export type RemoveAskPunttProjectMemberResponse = z.infer<
  typeof removeAskPunttProjectMemberResponseSchema
>;

// Set the areas a member owns (owner-only). Replaces that member's whole set:
// an omitted area is one they no longer own, so a client cannot leave a stale
// owner behind by sending only the additions.
export const setAskPunttProjectMemberAreasRequestSchema = z.object({
  params: z.object({ projectId: z.string(), userId: z.string() }),
  body: z.object({ owns: z.array(askPunttAreaSchema).max(20) }),
});
export const setAskPunttProjectMemberAreasResponseSchema = z.object({
  memberAreas: z.array(askPunttProjectMemberAreaSchema),
});
export type SetAskPunttProjectMemberAreasResponse = z.infer<
  typeof setAskPunttProjectMemberAreasResponseSchema
>;

// Add a file (creates version 1)
export const addAskPunttProjectFileRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
  body: z.object({
    name: z.string().min(1),
    type: z.enum(["image", "video", "file"]),
    key: z.string(),
    source: z.string().url(),
    description: z.string().optional(),
  }),
});
export const addAskPunttProjectFileResponseSchema = z.object({
  file: askPunttProjectFileSchema,
});
export type AddAskPunttProjectFileResponse = z.infer<
  typeof addAskPunttProjectFileResponseSchema
>;

// Add a new version to an existing file
export const addAskPunttProjectFileVersionRequestSchema = z.object({
  params: z.object({ projectId: z.string(), fileId: z.string() }),
  body: z.object({
    key: z.string(),
    source: z.string().url(),
    name: z.string().optional(),
    label: z.string().optional(),
    description: z.string().optional(),
  }),
});
export const addAskPunttProjectFileVersionResponseSchema = z.object({
  file: askPunttProjectFileSchema,
});
export type AddAskPunttProjectFileVersionResponse = z.infer<
  typeof addAskPunttProjectFileVersionResponseSchema
>;

// Commit a durable project instruction ("@Puntt remember …" / explicit memory)
export const commitAskPunttProjectInstructionRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
  body: z.object({
    text: z.string().min(1),
    scope: z.enum(["project", "enterprise"]).optional().default("project"),
    sourceChatId: z.string().optional(),
    sourceMessageId: z.string().optional(),
  }),
});
export const commitAskPunttProjectInstructionResponseSchema = z.object({
  instruction: askPunttProjectInstructionSchema,
});
export type CommitAskPunttProjectInstructionResponse = z.infer<
  typeof commitAskPunttProjectInstructionResponseSchema
>;

// Revoke (soft) a committed instruction (owner-only)
export const revokeAskPunttProjectInstructionRequestSchema = z.object({
  params: z.object({ projectId: z.string(), instructionId: z.string() }),
});
export const revokeAskPunttProjectInstructionResponseSchema = z.object({
  success: z.boolean(),
});
export type RevokeAskPunttProjectInstructionResponse = z.infer<
  typeof revokeAskPunttProjectInstructionResponseSchema
>;

// List chats in a project
export const listAskPunttProjectChatsRequestSchema = z.object({
  params: z.object({ projectId: z.string() }),
  query: z.object({
    limit: z.coerce.number().optional().default(50),
    offset: z.coerce.number().optional().default(0),
  }),
});
export const listAskPunttProjectChatsResponseSchema = z.object({
  chats: z.array(askPunttProjectChatSummarySchema),
  total: z.number().default(0),
});
export type ListAskPunttProjectChatsResponse = z.infer<
  typeof listAskPunttProjectChatsResponseSchema
>;

// Share a personal project chat with the whole project (owner of the chat
// only). Promotes the chat to a project-visible "shared" chat so every member
// can read it, and posts a note to the project feed.
export const shareAskPunttProjectChatRequestSchema = z.object({
  params: z.object({ projectId: z.string(), chatId: z.string() }),
});
export const shareAskPunttProjectChatResponseSchema = z.object({
  chat: askPunttProjectChatSummarySchema,
});
export type ShareAskPunttProjectChatResponse = z.infer<
  typeof shareAskPunttProjectChatResponseSchema
>;

// Attach an existing standalone chat to a project (owner-only)
export const addChatToAskPunttProjectRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
  body: z.object({ projectId: z.string() }),
});
export const addChatToAskPunttProjectResponseSchema = z.object({
  success: z.boolean(),
});
export type AddChatToAskPunttProjectResponse = z.infer<
  typeof addChatToAskPunttProjectResponseSchema
>;

// Detach a chat from its project (owner-only)
export const removeChatFromAskPunttProjectRequestSchema = z.object({
  params: z.object({ chatId: z.string() }),
});
export const removeChatFromAskPunttProjectResponseSchema = z.object({
  success: z.boolean(),
});
export type RemoveChatFromAskPunttProjectResponse = z.infer<
  typeof removeChatFromAskPunttProjectResponseSchema
>;
